import { useState, useEffect } from 'react'
import { Link, useOutletContext, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { getVehicles, updateVehicleVisibility, getBatchVehicleScoreCounts } from '../../lib/supabase'
import { calculateVehicleScore, getScoreTier, calculateCollectionScore } from '../../lib/garageScore'
import SignUpPrompt from '../../components/SignUpPrompt'
import {
  Car, ChevronRight, AlertCircle, Plus, ArrowRight, Bell, TrendingUp,
  Heart, Calendar, Wrench, Star, Headphones,
  Clock, Sparkles, Award, Target, Eye, EyeOff
} from 'lucide-react'

// Demo vehicles for guest users - photos match actual vehicles shown
const DEMO_VEHICLES = [
  {
    id: 'demo-1',
    year: 1969,
    make: 'Ford',
    model: 'Mustang Boss 302',
    color: 'Lime Gold',
    status: 'ready',
    mileage: 48500,
    primary_image_url: 'https://media.hagerty.com/media/wp-content/uploads/uscamediasite/images/story-images/2019/01/31/boss302_1969_10120190131150107'
  },
  {
    id: 'demo-2',
    year: 2020,
    make: 'Porsche',
    model: '911 Carrera S',
    color: 'White',
    status: 'ready',
    mileage: 12300,
    primary_image_url: 'https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?w=800&auto=format&fit=crop'
  },
  {
    id: 'demo-3',
    year: 2020,
    make: 'Chevrolet',
    model: 'Corvette C8 Stingray',
    color: 'Sebring Orange',
    status: 'stored',
    mileage: 8400,
    primary_image_url: 'https://cdn.motor1.com/images/mgl/Kk6J1/s1/2020-chevrolet-corvette-stingray.webp'
  }
]

// Get time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    ready: { className: 'badge-ready', label: 'Ready' },
    in_service: { className: 'badge-service', label: 'In Service' },
    stored: { className: 'badge-stored', label: 'Stored' },
    in_transit: { className: 'badge-transit', label: 'In Transit' },
  }

  const config = statusConfig[status] || statusConfig.stored

  return (
    <span className={`badge ${config.className}`}>
      {config.label}
    </span>
  )
}

// Tier badge component for Garage Score
const TierBadge = ({ score, compact = false }) => {
  const tier = getScoreTier(score)

  const bgColorMap = {
    'bg-gray-100': 'bg-gray-100',
    'bg-blue-100': 'bg-blue-100',
    'bg-green-100': 'bg-green-100',
    'bg-amber-100': 'bg-amber-100',
    'bg-orange-100': 'bg-orange-100',
  }

  const textColorMap = {
    'text-gray-700': 'text-gray-700',
    'text-blue-700': 'text-blue-700',
    'text-green-700': 'text-green-700',
    'text-amber-700': 'text-amber-700',
    'text-orange-700': 'text-orange-700',
  }

  if (compact) {
    return (
      <div className="flex items-center gap-1.5 mt-1">
        <div className={`w-2 h-2 rounded-full ${tier.bgClass}`} />
        <span className="text-[10px] font-medium text-rdc-taupe">{tier.name}</span>
      </div>
    )
  }

  return (
    <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full ${tier.bgClass}`}>
      <span className={`text-xs font-medium ${tier.textClass}`}>{tier.name}</span>
      <span className={`text-[10px] opacity-75 ${tier.textClass}`}>{score}%</span>
    </div>
  )
}

// Community activity removed — real activity feed coming soon

// Mini vehicle card for featured/horizontal scroll
const MiniVehicleCard = ({ vehicle, isDemo, onDemoClick, onToggleVisibility, vehicleScore }) => {
  const content = (
    <>
      <div className="vehicle-card-image h-36">
        {vehicle.primary_image_url ? (
          <img
            src={vehicle.primary_image_url}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Car size={32} className="text-rdc-stone" strokeWidth={1.25} />
          </div>
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1.5">
          {!isDemo && onToggleVisibility && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleVisibility(vehicle.id, !vehicle.is_public) }}
              className={`p-1.5 rounded-full backdrop-blur-sm transition-colors ${
                vehicle.is_public
                  ? 'bg-white/90 text-rdc-orange hover:bg-white'
                  : 'bg-black/40 text-white/70 hover:bg-black/60'
              }`}
              title={vehicle.is_public ? 'Public - click to hide' : 'Private - click to share'}
            >
              {vehicle.is_public ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
          )}
          <StatusBadge status={vehicle.status} />
        </div>
        {isDemo && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-sm font-medium">
              Sign up to add yours
            </span>
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="text-xs font-semibold text-rdc-taupe tracking-wide">{vehicle.year}</div>
        <h3 className="text-sm font-medium text-black truncate">
          {vehicle.make} {vehicle.model}
        </h3>
        {vehicleScore !== undefined && <TierBadge score={vehicleScore} compact={true} />}
      </div>
    </>
  )

  if (isDemo) {
    return (
      <button onClick={onDemoClick} className="flex-shrink-0 w-64 vehicle-card group text-left">
        {content}
      </button>
    )
  }

  return (
    <Link to={`/garage/${vehicle.id}`} className="flex-shrink-0 w-64 vehicle-card group">
      {content}
    </Link>
  )
}

// Full vehicle card
const VehicleCard = ({ vehicle, isDemo, onDemoClick, onToggleVisibility, vehicleScore }) => {
  const content = (
    <>
      <div className="vehicle-card-image">
        {vehicle.primary_image_url ? (
          <img
            src={vehicle.primary_image_url}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Car size={40} className="text-rdc-stone" strokeWidth={1.25} />
          </div>
        )}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {!isDemo && onToggleVisibility && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleVisibility(vehicle.id, !vehicle.is_public) }}
              className={`p-1.5 rounded-full backdrop-blur-sm transition-colors ${
                vehicle.is_public
                  ? 'bg-white/90 text-rdc-orange hover:bg-white'
                  : 'bg-black/40 text-white/70 hover:bg-black/60'
              }`}
              title={vehicle.is_public ? 'Public - click to hide' : 'Private - click to share'}
            >
              {vehicle.is_public ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          )}
          <StatusBadge status={vehicle.status} />
        </div>
        {isDemo && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-sm font-medium">
              Sign up to add yours
            </span>
          </div>
        )}
      </div>
      <div className="vehicle-card-content">
        <div className="vehicle-card-year">{vehicle.year}</div>
        <h3 className="vehicle-card-title">
          {vehicle.make} {vehicle.model}
        </h3>
        {vehicle.color && (
          <div className="vehicle-card-subtitle">{vehicle.color}</div>
        )}
        {vehicleScore !== undefined && (
          <div className="mt-2 mb-2">
            <TierBadge score={vehicleScore} compact={false} />
          </div>
        )}
        <div className="vehicle-card-meta">
          <span className="text-caption">
            {vehicle.mileage?.toLocaleString() || '—'} miles
          </span>
          <ChevronRight
            size={16}
            className="text-rdc-stone group-hover:text-rdc-orange group-hover:translate-x-0.5 transition-all"
            strokeWidth={2}
          />
        </div>
      </div>
    </>
  )

  if (isDemo) {
    return (
      <button onClick={onDemoClick} className="vehicle-card group text-left w-full">
        {content}
      </button>
    )
  }

  return (
    <Link to={`/garage/${vehicle.id}`} className="vehicle-card group">
      {content}
    </Link>
  )
}

// Empty state component
const EmptyState = ({ onAddVehicle }) => (
  <div className="card p-12 text-center max-w-md mx-auto">
    <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-rdc-cream flex items-center justify-center">
      <Car size={28} className="text-rdc-taupe" strokeWidth={1.5} />
    </div>
    <h3 className="section-title mb-3">Your garage awaits</h3>
    <p className="text-body text-rdc-charcoal mb-4">
      Start building your complete vehicle collection with detailed documentation.
    </p>
    <div className="mb-6 space-y-2 text-sm text-rdc-taupe text-left">
      <div className="flex items-start gap-2">
        <span className="text-rdc-orange font-bold">•</span>
        <span>Track every detail of your collection</span>
      </div>
      <div className="flex items-start gap-2">
        <span className="text-rdc-orange font-bold">•</span>
        <span>Build documented provenance that increases value</span>
      </div>
      <div className="flex items-start gap-2">
        <span className="text-rdc-orange font-bold">•</span>
        <span>Get a Provenance Score for each vehicle</span>
      </div>
    </div>
    <button
      onClick={onAddVehicle}
      className="btn-primary inline-flex items-center gap-2"
    >
      <Plus size={18} strokeWidth={2} />
      Add Your First Car
    </button>
  </div>
)

// Guest banner component
const GuestBanner = ({ onSignUp }) => (
  <div className="mb-6 p-4 bg-gradient-to-r from-rdc-primary/10 via-rdc-gold/10 to-rdc-primary/10 border border-rdc-primary/20 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-rdc-primary/20 flex items-center justify-center flex-shrink-0">
        <Sparkles size={20} className="text-rdc-primary" />
      </div>
      <div>
        <p className="font-medium text-black">You're viewing sample vehicles</p>
        <p className="text-sm text-rdc-taupe">Create a free account to add your own collection</p>
      </div>
    </div>
    <button
      onClick={onSignUp}
      className="btn-primary whitespace-nowrap"
    >
      Get Started Free
    </button>
  </div>
)


export default function Garage() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const outletContext = useOutletContext() || {}
  const isGuest = !user

  const [vehicles, setVehicles] = useState([])
  const [vehicleCounts, setVehicleCounts] = useState({})
  const [vehicleScores, setVehicleScores] = useState({})
  const [collectionScore, setCollectionScore] = useState(null)
  const [loading, setLoading] = useState(!isGuest)
  const [error, setError] = useState('')
  const [showAllVehicles, setShowAllVehicles] = useState(false)
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false)

  const greeting = getGreeting()
  const firstName = isGuest ? 'Guest' : (profile?.full_name?.split(' ')[0] || 'there')

  useEffect(() => {
    if (isGuest) {
      // Show demo vehicles for guests
      setVehicles(DEMO_VEHICLES)
      setLoading(false)
    } else {
      loadVehicles()
    }
  }, [user, isGuest])

  const loadVehicles = async () => {
    if (!user) return
    try {
      const { data, error } = await getVehicles(user.id)
      if (error) throw error
      setVehicles(data || [])

      // Get aggregate counts for scoring using shared helper
      const vehicleIds = (data || []).map(v => v.id)
      if (vehicleIds.length > 0) {
        try {
          const counts = await getBatchVehicleScoreCounts(vehicleIds)

          // Add photo counts from primary_image_url
          data.forEach(v => {
            if (!counts[v.id]) counts[v.id] = { services: 0, docs: 0, vals: 0 }
            counts[v.id].photos = v.primary_image_url ? 1 : 0
          })
          setVehicleCounts(counts)

          // Calculate scores
          const scores = {}
          const scoreObjects = []
          data.forEach(vehicle => {
            const vehicleCount = counts[vehicle.id] || { services: 0, docs: 0, vals: 0, photos: 0 }
            const scoreObj = calculateVehicleScore(
              vehicle,
              vehicleCount.services,
              vehicleCount.docs,
              vehicleCount.vals,
              vehicleCount.photos
            )
            scores[vehicle.id] = scoreObj.score
            scoreObjects.push(scoreObj)
          })
          setVehicleScores(scores)

          // Calculate collection score
          const collectionScoreObj = calculateCollectionScore(scoreObjects)
          setCollectionScore(collectionScoreObj)
        } catch (scoreErr) {
          console.error('Error calculating scores:', scoreErr)
          // Continue even if scoring fails
        }
      }
    } catch (err) {
      console.error('Error loading vehicles:', err)
      setError('Failed to load vehicles')
    } finally {
      setLoading(false)
    }
  }

  const handleAddVehicle = () => {
    if (isGuest) {
      setShowSignUpPrompt(true)
    } else {
      navigate('/garage/add')
    }
  }

  const handleToggleVisibility = async (vehicleId, newValue) => {
    // Optimistic update
    setVehicles(prev => prev.map(v => v.id === vehicleId ? { ...v, is_public: newValue } : v))
    try {
      const { error } = await updateVehicleVisibility(vehicleId, newValue)
      if (error) throw error
    } catch (err) {
      // Revert on failure
      setVehicles(prev => prev.map(v => v.id === vehicleId ? { ...v, is_public: !newValue } : v))
      console.error('Error updating visibility:', err)
    }
  }

  // Calculate collection stats
  const totalMiles = vehicles.reduce((sum, v) => sum + (v.mileage || 0), 0)
  const readyCount = vehicles.filter(v => v.status === 'ready').length
  const inServiceCount = vehicles.filter(v => v.status === 'in_service').length

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner" />
      </div>
    )
  }

  // Show empty state if no vehicles (only for logged in users with no vehicles)
  if (vehicles.length === 0 && !isGuest) {
    return (
      <div className="animate-fade-in">
        {/* Header */}
        <div className="mb-10">
          <p className="text-label mb-1">{greeting}</p>
          <h1 className="page-title">{firstName}'s Garage</h1>
        </div>
        <EmptyState onAddVehicle={handleAddVehicle} />
        <SignUpPrompt
          isOpen={showSignUpPrompt}
          onClose={() => setShowSignUpPrompt(false)}
          action="add your first vehicle"
        />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Guest Banner */}
      {isGuest && <GuestBanner onSignUp={() => setShowSignUpPrompt(true)} />}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-label mb-1">{greeting}</p>
          <h1 className="page-title">{isGuest ? 'Sample Garage' : `${firstName}'s Garage`}</h1>
        </div>
        <button onClick={handleAddVehicle} className="btn-primary">
          <Plus size={18} strokeWidth={2} />
          Add a Car
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-rdc-burgundy/5 border border-rdc-burgundy/20 rounded-xl flex items-center gap-3 text-rdc-burgundy">
          <AlertCircle size={18} strokeWidth={2} />
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        {/* Collection Summary - Left Column (spans 2) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Collection Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="card p-4 sm:p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rdc-orange/10 flex items-center justify-center flex-shrink-0">
                  <Car size={20} className="text-rdc-orange" />
                </div>
                <div className="flex-1 flex sm:block items-center justify-between">
                  <div className="text-xs text-rdc-taupe sm:order-last">Vehicles</div>
                  <div className="text-2xl font-semibold text-black">{vehicles.length}</div>
                </div>
              </div>
            </div>
            <div className="card p-4 sm:p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Target size={20} className="text-green-600" />
                </div>
                <div className="flex-1 flex sm:block items-center justify-between">
                  <div className="text-xs text-rdc-taupe sm:order-last">Road Ready</div>
                  <div className="text-2xl font-semibold text-black">{readyCount}</div>
                </div>
              </div>
            </div>
            <div className="card p-4 sm:p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rdc-saddle/10 flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={20} className="text-rdc-saddle" />
                </div>
                <div className="flex-1 flex sm:block items-center justify-between">
                  <div className="text-xs text-rdc-taupe sm:order-last">Total Miles</div>
                  <div className="text-2xl font-semibold text-black">{totalMiles.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Collection Score Summary */}
          {collectionScore && (
            <div className="card p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-black">Your Collection</h3>
                  <p className="text-xs text-rdc-taupe mt-0.5">{collectionScore.tierInfo.name} ({collectionScore.averageScore}%)</p>
                </div>
                <div className={`w-16 h-16 rounded-full ${collectionScore.tierInfo.bgClass} flex items-center justify-center`}>
                  <div className="text-center">
                    <div className={`text-lg font-semibold ${collectionScore.tierInfo.textClass}`}>{collectionScore.averageScore}%</div>
                  </div>
                </div>
              </div>
              <div className="w-full bg-rdc-stone/10 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${collectionScore.tierInfo.bgClass}`}
                  style={{ width: `${collectionScore.averageScore}%` }}
                />
              </div>
            </div>
          )}

          {/* Your Vehicles - Horizontal Scroll for quick view */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-label">Your Collection</h2>
              {vehicles.length > 3 && (
                <button
                  onClick={() => setShowAllVehicles(!showAllVehicles)}
                  className="text-xs text-rdc-orange font-medium hover:underline flex items-center gap-1"
                >
                  {showAllVehicles ? 'Show less' : `View all ${vehicles.length}`}
                  <ChevronRight size={14} />
                </button>
              )}
            </div>
            {showAllVehicles ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {vehicles.map((vehicle) => (
                  <MiniVehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    isDemo={isGuest}
                    onDemoClick={() => setShowSignUpPrompt(true)}
                    onToggleVisibility={handleToggleVisibility}
                    vehicleScore={vehicleScores[vehicle.id]}
                  />
                ))}
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
                {vehicles.slice(0, 4).map((vehicle) => (
                  <MiniVehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    isDemo={isGuest}
                    onDemoClick={() => setShowSignUpPrompt(true)}
                    onToggleVisibility={handleToggleVisibility}
                    vehicleScore={vehicleScores[vehicle.id]}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Link
              to="/maintenance"
              className="card p-4 group hover:border-rdc-orange/30 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-amber-100 flex items-center justify-center">
                <Bell size={18} className="text-amber-600" />
              </div>
              <div className="text-sm font-medium text-black">Maintenance</div>
              <p className="text-xs text-rdc-taupe mt-0.5">Check alerts</p>
            </Link>
            <Link
              to="/valuations"
              className="card p-4 group hover:border-rdc-orange/30 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp size={18} className="text-green-600" />
              </div>
              <div className="text-sm font-medium text-black">Valuations</div>
              <p className="text-xs text-rdc-taupe mt-0.5">Track value</p>
            </Link>
            <Link
              to="/service-history"
              className="card p-4 group hover:border-rdc-orange/30 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
                <Wrench size={18} className="text-blue-600" />
              </div>
              <div className="text-sm font-medium text-black">Service</div>
              <p className="text-xs text-rdc-taupe mt-0.5">Log records</p>
            </Link>
            <Link
              to="/specialists"
              className="card p-4 group hover:border-rdc-orange/30 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
                <Star size={18} className="text-purple-600" />
              </div>
              <div className="text-sm font-medium text-black">Specialists</div>
              <p className="text-xs text-rdc-taupe mt-0.5">Find experts</p>
            </Link>
          </div>
        </div>

        {/* Right Column - Activity Feed */}
        <div className="space-y-6">
          {/* Concierge CTA */}
          <Link to="/concierge" className="card p-5 bg-gradient-to-br from-rdc-charcoal to-rdc-charcoal/90 border-rdc-orange/30 block hover:shadow-lg transition-shadow group">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-rdc-orange/20 flex items-center justify-center group-hover:bg-rdc-orange/30 transition-colors">
                <Headphones size={18} className="text-rdc-orange" />
              </div>
              <div>
                <h3 className="font-medium text-white">Need Something?</h3>
                <p className="text-xs text-rdc-stone">Transport, service, inspections</p>
              </div>
            </div>
            <div className="mt-3 text-sm text-rdc-orange font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
              Start a Request
              <ArrowRight size={14} />
            </div>
          </Link>

          {/* Find Specialists CTA */}
          <Link
            to="/specialists"
            className="card p-5 bg-gradient-to-br from-rdc-orange/5 via-white to-rdc-cream border-rdc-orange/20 block hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-rdc-orange/10 flex items-center justify-center">
                <Award size={20} className="text-rdc-orange" />
              </div>
              <div>
                <h3 className="font-medium text-black">Need Service?</h3>
                <p className="text-xs text-rdc-taupe">Find vetted specialists near you</p>
              </div>
            </div>
            <div className="mt-3 text-sm text-rdc-orange font-medium flex items-center gap-1">
              Browse Directory
              <ArrowRight size={14} />
            </div>
          </Link>

        </div>
      </div>

      {/* Featured Vehicle - Show if user has vehicles */}
      {vehicles.length > 0 && vehicles[0].primary_image_url && (
        <div className="mb-10">
          <h2 className="text-label mb-4">{isGuest ? 'Featured Example' : 'Featured in Your Collection'}</h2>
          {isGuest ? (
            <button
              onClick={() => setShowSignUpPrompt(true)}
              className="card overflow-hidden block group w-full text-left"
            >
              <div className="img-container aspect-hero relative">
                <img
                  src={vehicles[0].primary_image_url}
                  alt={`${vehicles[0].year} ${vehicles[0].make} ${vehicles[0].model}`}
                  className="transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-lg font-medium bg-rdc-primary px-6 py-3 rounded-full">
                    Create your garage
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="text-xs font-semibold tracking-wider opacity-75 mb-1">
                    {vehicles[0].year}
                  </div>
                  <h3 className="font-display text-2xl font-medium mb-1">
                    {vehicles[0].make} {vehicles[0].model}
                  </h3>
                  {vehicles[0].color && (
                    <p className="text-sm opacity-75">{vehicles[0].color}</p>
                  )}
                </div>
                <div className="absolute top-4 right-4">
                  <StatusBadge status={vehicles[0].status} />
                </div>
              </div>
            </button>
          ) : (
            <div className="card overflow-hidden relative">
              <Link
                to={`/garage/${vehicles[0].id}`}
                className="block group"
              >
                <div className="img-container aspect-hero relative">
                  <img
                    src={vehicles[0].primary_image_url}
                    alt={`${vehicles[0].year} ${vehicles[0].make} ${vehicles[0].model}`}
                    className="transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <div className="text-xs font-semibold tracking-wider opacity-75 mb-1">
                      {vehicles[0].year}
                    </div>
                    <h3 className="font-display text-2xl font-medium mb-1">
                      {vehicles[0].make} {vehicles[0].model}
                    </h3>
                    {vehicles[0].color && (
                      <p className="text-sm opacity-75">{vehicles[0].color}</p>
                    )}
                  </div>
                </div>
              </Link>
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <button
                  onClick={() => handleToggleVisibility(vehicles[0].id, !vehicles[0].is_public)}
                  className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
                    vehicles[0].is_public
                      ? 'bg-white/90 text-rdc-orange hover:bg-white'
                      : 'bg-black/40 text-white/70 hover:bg-black/60'
                  }`}
                  title={vehicles[0].is_public ? 'Public - click to hide' : 'Private - click to share'}
                >
                  {vehicles[0].is_public ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
                <StatusBadge status={vehicles[0].status} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* All Vehicles Grid - Show below if more than 1 vehicle and first has image */}
      {vehicles.length > 1 && !showAllVehicles && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-label">All Vehicles</h2>
            <span className="text-xs text-rdc-taupe">{vehicles.length} total</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {vehicles.slice(vehicles[0].primary_image_url ? 1 : 0).map((vehicle) => (
              <VehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                isDemo={isGuest}
                onDemoClick={() => setShowSignUpPrompt(true)}
                onToggleVisibility={handleToggleVisibility}
                vehicleScore={vehicleScores[vehicle.id]}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sign Up Prompt Modal */}
      <SignUpPrompt
        isOpen={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        action="add your vehicles"
      />
    </div>
  )
}
